import React, { useEffect, useRef, useState } from "react";
import { APIProvider, Map, useMap } from "@vis.gl/react-google-maps";
import { MapPin } from "lucide-react";
import { DEFAULT_CENTER } from "../../lib/constants";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Colour per live item type so ops can read the city at a glance.
const COLORS = { ride: "#1D4ED8", delivery: "#D97706", private_car: "#7C3AED" };

const dotIcon = (color) => ({
  url:
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(
      `<svg viewBox="0 0 28 36" width="28" height="36" xmlns="http://www.w3.org/2000/svg"><path d="M14 0C6.3 0 0 6.2 0 13.9 0 24.3 14 36 14 36s14-11.7 14-22.1C28 6.2 21.7 0 14 0z" fill="${color}"/><circle cx="14" cy="14" r="5" fill="#FFFFFF"/></svg>`,
    ),
  w: 28, h: 36, ax: 14, ay: 34,
});

// Pick the best coordinate for an item: live driver GPS first, else pickup/delivery.
function coordFor(item) {
  if (Number.isFinite(item.driver_lat) && Number.isFinite(item.driver_lng)) return { lat: item.driver_lat, lng: item.driver_lng, live: true };
  if (item.type === "ride" && Number.isFinite(item.pickup_lat)) return { lat: item.pickup_lat, lng: item.pickup_lng, live: false };
  if (item.type === "delivery" && Number.isFinite(item.delivery_lat)) return { lat: item.delivery_lat, lng: item.delivery_lng, live: false };
  if (item.type === "private_car" && Number.isFinite(item.pickup_lat)) return { lat: item.pickup_lat, lng: item.pickup_lng, live: false };
  return null;
}

function Markers({ items, onPick }) {
  const map = useMap();
  const markersRef = useRef({});   // id -> google Marker
  const fittedRef = useRef(false);
  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google.maps;
    const seen = new Set();
    const bounds = new g.LatLngBounds();
    let n = 0;
    items.forEach((item) => {
      const c = coordFor(item);
      if (!c) return;
      n += 1;
      seen.add(item.id);
      bounds.extend({ lat: c.lat, lng: c.lng });
      const existing = markersRef.current[item.id];
      if (existing) {
        existing.setPosition({ lat: c.lat, lng: c.lng });  // move in place (auto-follow)
      } else {
        const ic = dotIcon(COLORS[item.type] || "#111827");
        const marker = new g.Marker({
          position: { lat: c.lat, lng: c.lng }, map,
          icon: { url: ic.url, scaledSize: new g.Size(ic.w, ic.h), anchor: new g.Point(ic.ax, ic.ay) },
          title: `${item.type} · ${item.customer_name || ""}`,
        });
        marker.addListener("click", () => onPick(item));
        markersRef.current[item.id] = marker;
      }
    });
    Object.keys(markersRef.current).forEach((id) => {
      if (!seen.has(id)) { markersRef.current[id].setMap(null); delete markersRef.current[id]; }
    });
    if (n > 0 && !fittedRef.current) {
      fittedRef.current = true;
      if (n === 1) { map.setCenter(bounds.getCenter()); map.setZoom(14); }
      else map.fitBounds(bounds, 60);
    }
  }, [map, items, onPick]);
  useEffect(() => () => { Object.values(markersRef.current).forEach((m) => m.setMap(null)); markersRef.current = {}; }, []);
  return null;
}

export default function LiveOrdersMap({ items = [], onPick, className = "" }) {
  const [plottable] = useState(() => items);
  if (!KEY) {
    return (
      <div className={`flex min-h-[420px] flex-col items-center justify-center gap-2 rounded-2xl border border-surface-300 bg-surface-100 ${className}`} data-testid="live-orders-map">
        <MapPin className="h-6 w-6 text-brand" /><p className="text-sm text-ink-muted">Map unavailable</p>
      </div>
    );
  }
  const hasAny = items.some((i) => coordFor(i));
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-surface-300 ${className}`} data-testid="live-orders-map">
      <APIProvider apiKey={KEY}>
        <Map defaultCenter={{ lat: DEFAULT_CENTER.lat, lng: DEFAULT_CENTER.lng }} defaultZoom={13}
          style={{ width: "100%", height: "100%", minHeight: 480 }} gestureHandling="greedy" disableDefaultUI zoomControl clickableIcons={false}>
          <Markers items={items} onPick={onPick} />
        </Map>
      </APIProvider>
      <div className="absolute left-3 top-3 flex flex-col gap-1 rounded-xl bg-white/90 px-3 py-2 text-xs shadow">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: COLORS.ride }} /> Rides</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: COLORS.delivery }} /> Deliveries</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full" style={{ background: COLORS.private_car }} /> Private cars</span>
      </div>
      {!hasAny && <div className="absolute inset-x-0 bottom-3 mx-auto w-max rounded-full bg-white/90 px-3 py-1 text-xs text-ink-muted shadow">No GPS/pin coordinates for current live orders yet</div>}
    </div>
  );
}
